import { msg } from '@lingui/core/macro';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import {
    RATING_SCALE_TABLE_HEADER_CELL_CLASS_NAME,
    RATING_SCALE_TABLE_ID,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/constants.ts';
import {
    SHARED_RATING_SCALE_ACTS_ROLE_MODEL_GUIDES,
    SHARED_RATING_SCALE_CONSISTENTLY_EXCEEDS_REQUIRED_STANDARD,
    SHARED_RATING_SCALE_DESCRIPTION,
    SHARED_RATING_SCALE_DOES_NOT_MEET_EXPECTED,
    SHARED_RATING_SCALE_DOES_NOT_MEET_REQUIRED,
    SHARED_RATING_SCALE_MEETS_EXPECTED_CRITERIA_GUIDELINES,
    SHARED_RATING_SCALE_PARTIALLY_MEETS_REQUIRED_STANDARD,
    SHARED_RATING_SCALE_PERFORMS_ABOVE_REQUIRED_STANDARD,
    SHARED_RATING_SCALE_PERFORMS_REQUIRED_STANDARD,
    SHARED_RATING_SCALE_POINTS,
    SHARED_RATING_SCALE_RATING,
    SHARED_RATING_SCALE_RATING_APPROVED,
    SHARED_RATING_SCALE_RATING_DISAPPROVED,
    SHARED_RATING_SCALE_RATING_EXCEEDS_EXPECTATIONS,
    SHARED_RATING_SCALE_RATING_EXCEPTIONAL,
    SHARED_RATING_SCALE_RATING_MEETS_EXPECTATIONS,
    SHARED_RATING_SCALE_RATING_NEEDS_IMPROVEMENT,
    SHARED_RATING_SCALE_RATING_UNSATISFACTORY,
    SHARED_RATING_SCALE_RELIABLE_NORMAL_SITUATIONS,
    SHARED_RATING_SCALE_REQUIRES_GUIDANCE_SUPERVISION,
    SHARED_RATING_SCALE_UNABLE_PERFORM_TASK_CORRECTLY,
    SHARED_RATING_SCALE_WORKS_INDEPENDENTLY_CONFIDENTLY,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/constants.ts';
import {
    Cell,
    DescriptionCell,
    HeaderCell,
    HeaderRow,
    Rating,
    Row,
    Table,
    Text,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/styled.tsx';
import { translateItem } from '@utils/translations.ts';

type RatingScaleItems = typeof FIVE_POINT_SCALE | typeof TWO_POINT_SCALE | typeof TWO_POINT_CONFIDENCE_SCALE;

const SUCCESS = 'success' as const;
const DEFAULT = 'default' as const;
const ERROR = 'error' as const;

const RATING_SCALE_HEADERS = [SHARED_RATING_SCALE_RATING, SHARED_RATING_SCALE_POINTS, SHARED_RATING_SCALE_DESCRIPTION];

const FIVE_POINT_SCALE = [
    {
        rating: SHARED_RATING_SCALE_RATING_EXCEPTIONAL,
        points: 5,
        type: SUCCESS,
        descriptions: [
            SHARED_RATING_SCALE_CONSISTENTLY_EXCEEDS_REQUIRED_STANDARD,
            SHARED_RATING_SCALE_ACTS_ROLE_MODEL_GUIDES,
        ],
    },
    {
        rating: SHARED_RATING_SCALE_RATING_EXCEEDS_EXPECTATIONS,
        points: 4,
        type: SUCCESS,
        descriptions: [
            SHARED_RATING_SCALE_PERFORMS_ABOVE_REQUIRED_STANDARD,
            SHARED_RATING_SCALE_WORKS_INDEPENDENTLY_CONFIDENTLY,
        ],
    },
    {
        rating: SHARED_RATING_SCALE_RATING_MEETS_EXPECTATIONS,
        points: 3,
        type: DEFAULT,
        descriptions: [SHARED_RATING_SCALE_PERFORMS_REQUIRED_STANDARD, SHARED_RATING_SCALE_RELIABLE_NORMAL_SITUATIONS],
    },
    {
        rating: SHARED_RATING_SCALE_RATING_NEEDS_IMPROVEMENT,
        points: 2,
        type: ERROR,
        descriptions: [
            SHARED_RATING_SCALE_PARTIALLY_MEETS_REQUIRED_STANDARD,
            SHARED_RATING_SCALE_REQUIRES_GUIDANCE_SUPERVISION,
        ],
    },
    {
        rating: SHARED_RATING_SCALE_RATING_UNSATISFACTORY,
        points: 1,
        type: ERROR,
        descriptions: [SHARED_RATING_SCALE_DOES_NOT_MEET_REQUIRED, SHARED_RATING_SCALE_UNABLE_PERFORM_TASK_CORRECTLY],
    },
];

const TWO_POINT_SCALE = [
    {
        rating: SHARED_RATING_SCALE_RATING_APPROVED,
        points: 1,
        type: SUCCESS,
        descriptions: [SHARED_RATING_SCALE_MEETS_EXPECTED_CRITERIA_GUIDELINES],
    },
    {
        rating: SHARED_RATING_SCALE_RATING_DISAPPROVED,
        points: 0,
        type: ERROR,
        descriptions: [SHARED_RATING_SCALE_DOES_NOT_MEET_EXPECTED],
    },
];

const TWO_POINT_CONFIDENCE_SCALE = [
    {
        rating: msg`Confident`,
        points: 1,
        type: SUCCESS,
        descriptions: [msg`Demonstrates confidence in performing the task according to the guidelines.`],
    },
    {
        rating: msg`Not Confident`,
        points: 0,
        type: ERROR,
        descriptions: [
            msg`Does not demonstrate sufficient confidence in performing the task according to the guidelines.`,
        ],
    },
];

const renderScale = (items: RatingScaleItems) => (
    <Table id={RATING_SCALE_TABLE_ID}>
        <HeaderRow>
            {RATING_SCALE_HEADERS.map((header) => (
                <HeaderCell key={header.id} className={RATING_SCALE_TABLE_HEADER_CELL_CLASS_NAME}>
                    {translateItem(header)}
                </HeaderCell>
            ))}
        </HeaderRow>

        {items.map(({ rating, points, type, descriptions }) => (
            <Row key={points}>
                <Cell>
                    <Rating $type={type}>{translateItem(rating)}</Rating>
                </Cell>
                <Cell>
                    <Text>{points}</Text>
                </Cell>
                <DescriptionCell>
                    <ul>
                        {descriptions.map((description) => (
                            <li key={description.id}>
                                <Text>{translateItem(description)}</Text>
                            </li>
                        ))}
                    </ul>
                </DescriptionCell>
            </Row>
        ))}
    </Table>
);

export const renderFivePointScaleRating = () => renderScale(FIVE_POINT_SCALE);

export const renderTwoPointScaleRating = () => renderScale(TWO_POINT_SCALE);

export const renderTwoPointConfidenceScaleRating = () => renderScale(TWO_POINT_CONFIDENCE_SCALE);

export const RATING_SCALE_RENDERERS: Record<TaskScaleType, () => React.ReactNode> = {
    [TASK_SCALE_TYPE.TwoPointApprove]: renderTwoPointScaleRating,
    [TASK_SCALE_TYPE.TwoPointConfidence]: renderTwoPointConfidenceScaleRating,
    [TASK_SCALE_TYPE.FivePointCompetence]: renderFivePointScaleRating,
};
